import MomentUtils from "@date-io/moment";
import moment from "moment";
import React, { useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import "moment/locale/pt";
import {
	Avatar,
	Box,
	Divider,
	Grid,
	InputAdornment,
	Table,
	TableBody,
	TableCell,
	TableFooter,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@material-ui/core";
import { makeStyles, withStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import Skeleton from "@material-ui/lab/Skeleton";
import {
	KeyboardDatePicker,
	MuiPickersUtilsProvider,
} from "@material-ui/pickers";
import clsx from "clsx";
import DynamicIcon from "./../../commons/DynamicIcon";
import consts from "../../main/consts";
import {
	getAllCounts,
	setPegueFilter,
	setReRender,
} from "../../state/order/actions";
import { calcTempoConfirmacao } from "./helpers";
import Filtro from "./helpers/Filtro";
import Legendas from "./Legendas";
import TablePaginationActions from "./TablePaginationActions";

const StyledTableCell = withStyles((theme) => ({
	root: {
		borderBottom: "none",
	},
	head: {
		backgroundColor: theme.palette.action.activatedOpacity,
		color: "#888888",
	},
	body: {
		fontSize: 14,
	},
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
	root: {
		backgroundColor: theme.palette.action.hover,
		cursor: "pointer",
		transition: theme.transitions.create(["all"], {
			easing: theme.transitions.easing.easeInOut,
			duration: theme.transitions.duration.leavingScreen,
		}),
		"&:hover": {
			transform: "scale(1.01)",
		},
		"& td:last-child": {
			borderRadius: "0 4px 4px 0",
		},
	},
}))(TableRow);

const useStyles = makeStyles((theme) => ({
  header: {
    minHeight: 64,
  },
  compactHeadCell: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  table: {
    backgroundColor: "#FAFAFA",
    borderCollapse: "separate",
    border: "none",
    //textTransform: 'uppercase'
  },
  rowHead: {
    borderRadius: "4px 0 0 4px",
    borderLeft: "4px solid",
    borderColor: (props) => theme.palette.pages[props.page],
  },
  spacer: {
    height: "1em",
    cursor: "pointer",
  },
  divider: {
    margin: 0,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  skeletons: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "100%",
  },
  skeleton: {
    backgroundColor: "#rgba(0, 0, 0, 0.04)",
    borderRadius: "5px",
  },
  contentFieldFilter: {
    paddingLeft: theme.spacing(2),
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down("xs")]: {
      paddingLeft: 0,
    },
  },
  contentNotFound: {
    borderBottom: 0,
  },
  boxNotFound: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing(6),
  },
  contentIcon: {
    width: 70,
    height: 70,
    backgroundColor: theme.palette.grey[200],
    marginBottom: theme.spacing(2),
  },
  icon: {
    width: 46,
    height: 46,
    color: theme.palette.grey[400],
  },
}));

const renderPaginator = (showPaginator, props) => {
	if (showPaginator) {
		return (
			<Table className={"responsive-table"}>
				<TableFooter>
					<TableRow>
						<TablePagination
							rowsPerPageOptions={[]}
							colSpan={props.headers.length}
							count={props.total}
							rowsPerPage={props.rowsPerPage}
							page={props.selectedPage}
							SelectProps={{
								inputProps: { "aria-label": "rows per page" },
								native: true,
							}}
							labelDisplayedRows={({ from, to, count }) =>
								`${from}-${to} de ${count}`
							}
							onChangePage={() => {}}
							onChangeRowsPerPage={() => {}}
							ActionsComponent={() => {
								return (
									<TablePaginationActions
										count={props.total}
										page={props.selectedPage}
										rowsPerPage={props.rowsPerPage}
										onChangePage={props.onHandlePageChange}
									/>
								);
							}}
						/>
					</TableRow>
				</TableFooter>
			</Table>
		);
	} else {
		return <></>;
	}
};

export default function ContentTable(props) {
  const {
    title,
    subtitle,
    headers,
    page,
    filter,
    loaded,
    rows,
    store,
    selectedOrigemFilter,
	hideLegendas,
    hideHeader,
  } = props;

	const {
		enable: enableOrdering,
		query,
		dataInicial,
		handleOrderChange,
		handleFilterChange,
		handleDateChange,
	} = filter || {};

	const dispatch = useDispatch();
	const classes = useStyles({ page });
	const [order, setOrder] = React.useState("asc");
	const [limitReq, setLimitReq] = React.useState(props.limit);
	const [reRenderValidation, setReRenderValidation] = React.useState(false);
	const [orderBy, setOrderBy] = React.useState("ordemNumero");

	React.useEffect(() => {
		if (props.limit === 10 && !reRenderValidation) return;
		dispatch(setPegueFilter(limitReq));
		dispatch(setReRender(true));
		setReRenderValidation(false);
	}, [limitReq, reRenderValidation]);
	const observer = useRef();
	const lastRowElement = useCallback(
		(node) => {
			if (!loaded) return;
			if (observer.current) observer.current.disconnect();
			observer.current = new IntersectionObserver((entries) => {
				if (entries[0].isIntersecting) {
					setLimitReq((prev) => prev + 20);
					setReRenderValidation(true);
				}
			});
			if (node) observer.current.observe(node);
		},
		[loaded],
	);
	React.useEffect(() => {
		const interval = setInterval(() => {
			props.onHandlePageChange(props.selectedPage || 0, null, {}, true);
			dispatch(getAllCounts());
		}, consts.POOLING_LIST_DELAY || 15000);

		return () => {
			clearInterval(interval);
		};
	});

	const handleRequestSort = (prop) => {
		const propToParam = {
			numero: "ordemNumero",
			dataInclusao: "ordemDataInclusao",
			cliente: "ordemNomeCliente",
			associado: "ordemNomeAssociado",
			origem: "ordemOrigem",
			entrega: "ordemEntrega",
			status: "ordemStatus",
			valorTotal: "ordemValorTotal",
			tipoEntrega: "ordemTipoEntrega",
			codigoExterno: "ordemCodigoExterno",
		};

		const param = propToParam[prop] || prop;
		const isAsc = orderBy === prop && order === "asc";
		const orderDirection = isAsc ? "desc" : "asc";
		setOrder(orderDirection);
		setOrderBy(prop);
		handleOrderChange instanceof Function &&
			handleOrderChange({ order: orderDirection, orderBy: param });
	};

	const getHeaderFilter = (header) => (
		<Filtro
			label={header.title}
			filterField={header.filter}
			filterOptions={header.filterOptions}
		/>
	);

  return (
    <>
      {!hideHeader && (
      <>
      <div className={classes.header}>
        <Box flexGrow={1}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={hideLegendas ? 12 : 5}>
              <Typography variant="h5">{title}</Typography>
              <Typography variant="subtitle1"><span dangerouslySetInnerHTML={{__html: subtitle}} /></Typography>
              {selectedOrigemFilter === "" && <p>Exibindo todos os pedidos</p>}

							{selectedOrigemFilter !== "" && (
								<p>
									<b>{rows.length} resultado(s)</b> para pedidos de origem{" "}
									<b>"{selectedOrigemFilter}"</b>
								</p>
							)}
						</Grid>
						{enableOrdering && (
							<Grid item xs={12} sm={3} className={classes.contentFieldFilter}>
								<MuiPickersUtilsProvider
									libInstance={moment}
									utils={MomentUtils}
									locale={"pt"}
								>
									<KeyboardDatePicker
										disableToolbar
										autoOk
										fullWidth
										format="DD/MM/YYYY"
										margin="dense"
										id="date-picker-historico"
										label="Data:"
										value={dataInicial}
										onChange={handleDateChange}
										inputVariant="outlined"
										InputLabelProps={{
											shrink: true,
										}}
										InputProps={{
											margin: "dense",
										}}
									/>
								</MuiPickersUtilsProvider>
							</Grid>
						)}
						{enableOrdering && (
							<Grid item xs={12} sm={3} className={classes.contentFieldFilter}>
								<TextField
									id="input-with-icon-textfield"
									label="Buscar:"
									value={query}
									fullWidth
									onChange={(e) => handleFilterChange(e.target.value)}
									variant="outlined"
									margin="dense"
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
									}}
								/>
							</Grid>
						)}
						{!enableOrdering && !hideLegendas && (
							<Grid item xs={12} sm={7} className={classes.contentFieldFilter}>
								<Legendas title={title} />
							</Grid>
						)}
					</Grid>
				</Box>
			</div>

      <Divider variant="middle" className={classes.divider} />
      </>
      )}
      {rows.length > 0 && renderPaginator(props.showPaginator, props)}
      <Table className={clsx(classes.table, "responsive-table")}>
        {!(loaded && rows.length === 0 && props.hideHeadersWhenEmpty) && (
        <TableHead>
          <TableRow>
            {headers &&
              headers.map((header, index) => {
                return (
                  <StyledTableCell
                    key={header.dataField + "-" + index}
                    align={header.align}
                    className={props.compactHeader ? classes.compactHeadCell : undefined}
                    sortDirection={orderBy === header.dataField ? order : false}
                  >
                    <TableSortLabel
                      active={enableOrdering && orderBy === header.dataField}
                      direction={orderBy === header.dataField ? order : "asc"}
                      onClick={(e) =>
                        !e.defaultPrevented &&
                        handleRequestSort(header.dataField)
                      }
                      hideSortIcon={!filter}
                    >
                      {header.filter ? getHeaderFilter(header) : header.title}
                      {orderBy === header.dataField ? (
                        <span className={classes.visuallyHidden}>
                          {order === "desc"
                            ? "sorted descending"
                            : "sorted ascending"}
                        </span>
                      ) : null}
                    </TableSortLabel>
                  </StyledTableCell>
                );
              })}
          </TableRow>
        </TableHead>
        )}
        {loaded && rows.length === 0 ? (
          <TableBody>
            <TableRow>
              <TableCell
                colSpan={props.headers.length}
                className={classes.contentNotFound}
              >
                {props.emptyState || (
                  <div className={classes.boxNotFound}>
                    <Avatar className={classes.contentIcon}>
                      <DynamicIcon icon={props.page} className={classes.icon} />
                    </Avatar>
                    <Typography variant="subtitle2">Não há pedidos!</Typography>
                  </div>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        ) : (
          <TableBody>
            {loaded &&
              rows.map((row, index) => {
                const calcConfirmacao = calcTempoConfirmacao(
                  row.tempoConfirmacao
                );
                if (index + 1 === rows.length && index + 1 !== props.total) {
                  return (
                    <React.Fragment key={row.numero}>
                      <TableRow
                        ref={lastRowElement}
                        className={classes.spacer}
                      />
                      <StyledTableRow onClick={(e) => props.onClickRow(row, e)}>
                        {headers.map((header, index) => {
                          const data = row[header.dataField];
                          if (index === 0) {
                            return (
                              <StyledTableCell
                                component="td"
                                data-label={header.title}
                                scope="row"
                                className={classes.rowHead}
                                key={header.dataField + "-" + index}
                                style={
                                  calcConfirmacao.isOld &&
                                  row.origem !== "PEGUE_NA_LOJA"
                                    ? {
                                        borderColor: "#e60c14",
                                        color: "#e60c14",
                                      }
                                    : null
                                }
                              >
                                {header.render instanceof Function
                                  ? header.render(data, row)
                                  : data}
                              </StyledTableCell>
                            );
                          }
                          return (
                            <StyledTableCell
                              data-label={header.title}
                              key={header.dataField + "-" + index}
                              align={header.align}
                              className={header.class}
                            >
                              {header.render instanceof Function
                                ? header.render(data, row)
                                : data}
                              {header.dataField === "tempoParaConfirmacao" &&
                                calcConfirmacao.dateTime}
                            </StyledTableCell>
                          );
                        })}
                      </StyledTableRow>
                      {/*<TableRow>*/}
                      {/*  <StyledTableCell ><Typography>Carregando pedidos...</Typography></StyledTableCell>*/}
                      {/*</TableRow>*/}
                    </React.Fragment>
                  );
                } else {
                  return (
                    <React.Fragment key={row.numero}>
                      <TableRow className={classes.spacer} />
                      <StyledTableRow onClick={(e) => props.onClickRow(row, e)}>
                        {headers.map((header, index) => {
                          const data = row[header.dataField];
                          if (index === 0) {
                            return (
                              <StyledTableCell
                                component="td"
                                data-label={header.title}
                                scope="row"
                                className={classes.rowHead}
                                key={header.dataField + "-" + index}
                                style={
                                  calcConfirmacao.isOld &&
                                  row.origem !== "PEGUE_NA_LOJA"
                                    ? {
                                        borderColor: "#e60c14",
                                        color: "#e60c14",
                                      }
                                    : null
                                }
                              >
                                {header.render instanceof Function
                                  ? header.render(data, row)
                                  : data}
                                {header.dataField === "tempoParaConfirmacao" &&
                                  calcConfirmacao.dateTime}
                              </StyledTableCell>
                            );
                          }

													return (
														<StyledTableCell
															data-label={header.title}
															key={header.dataField + "-" + index}
															align={header.align}
															className={header.class}
														>
															{header.render instanceof Function
																? header.render(data, row)
																: data}
															{header.dataField === "tempoParaConfirmacao" &&
																calcConfirmacao.dateTime}
														</StyledTableCell>
													);
												})}
											</StyledTableRow>
										</React.Fragment>
									);
								}
							})}
					</TableBody>
				)}
			</Table>
			{rows.length > 0 && renderPaginator(props.showPaginator, props)}
			{!loaded && (
				<div className={classes.skeletons}>
					{[1, 2, 3].map((i) => (
						<React.Fragment key={i}>
							<div className={classes.spacer} />
							<span>
								<Skeleton
									variant="rect"
									height={52}
									className={classes.skeleton}
								/>
							</span>
						</React.Fragment>
					))}
				</div>
			)}
		</>
	);
}
